/**
 * Premium calculation utilities for rate schedules
 * Used by SummaryGrid to compute monthly/annual premiums
 */

import { CoverageTierEnum, RateUnitEnum } from "@platform/openapi-types";

import {
  getAdndExposureFromPrimarySchedule,
  getRateDivisor,
  getRateExposure,
} from "./adnd-rate-resolution";
import { isChildCompanionSchedule } from "./schedule-helpers";

import type { Rate, RateSchedule } from "../types";

/**
 * Resolves exposure for a non-volume-based unit (usd flat rates, e.g. a
 * per-unit dependent count): numberOfLives first, falling back to volume only
 * when numberOfLives is genuinely absent — the legacy case of a "usd" unit
 * that was actually mis-stored and means volumePer1000. Preferring volume
 * unconditionally (as getRateExposure does) would let a stray volume value
 * (even 0, e.g. left over from a prior volume-based edit) silently outrank a
 * correctly-set numberOfLives and zero the exposure.
 */
const nonVolumeUnitExposure = (rate: Rate): number | undefined =>
  rate.numberOfLives != null ? rate.numberOfLives : rate.volume;

// Same stored-data correction as computeVolumeUnitRatePremium: a "usd"
// unit with an explicit volume and no numberOfLives was stored
// incorrectly and means volumePer1000.
const effectiveUnit = (rate: Rate): string | undefined =>
  rate.unit === RateUnitEnum.usd &&
  rate.volume != null &&
  rate.numberOfLives == null
    ? RateUnitEnum.volumePer1000
    : rate.unit;

const computeVolumeUnitRatePremium = (rate: Rate): number => {
  // Volume-based units (volumePer*, per*OfSalary) price off the covered
  // benefit/payroll volume only. numberOfLives is a distinct exposure used by
  // USD flat rates (rate × lives) and must not substitute for a missing volume —
  // doing so generated incorrect STD/LTD premiums with no covered-benefit input
  // (DP-4094).
  const volume = isVolumeBasedUnit(rate.unit)
    ? rate.volume
    : nonVolumeUnitExposure(rate);
  if (!rate.rate || !volume) return 0;
  // If unit is "usd" but volume is explicitly set and there's no numberOfLives,
  // the unit was stored incorrectly. Correct to volumePer1000 so the divisor
  // is applied as intended.

  const divisor = getRateDivisor(effectiveUnit(rate));
  return rate.rate * (volume / divisor);
};

/**
 * Compute monthly premium for a flat rate schedule.
 * Sums premiums across all individual rates (e.g., gender/tobacco splits,
 * coverage tiers, or companion rates).
 *
 * Individual Rate Premium = rate × (volume / rate_unit_divisor)
 */
export const computeFlatRatePremium = (rateSchedule: RateSchedule): number => {
  const rates = rateSchedule.rates;
  if (!rates || rates.length === 0) return 0;

  return rates.reduce(
    (total, rate) => total + computeVolumeUnitRatePremium(rate),
    0,
  );
};

/**
 * Compute monthly premium for a child companion (childOnly flat rate) schedule.
 * The grid's child Volume input stores the exposure in numberOfLives, so these
 * schedules price off volume-or-lives — unlike other volume-based flat rates,
 * which price off volume only (DP-4094). Excluding numberOfLives here dropped
 * Child Vol Life from voluntary life premium totals (PB-675).
 */
export const computeChildCompanionPremium = (
  rateSchedule: RateSchedule,
): number => {
  const rates = rateSchedule.rates || [];
  return rates.reduce((total, rate) => {
    const exposure = getRateExposure(rate);
    if (!rate.rate || !exposure) return total;

    return total + rate.rate * (exposure / getRateDivisor(effectiveUnit(rate)));
  }, 0);
};

/**
 * Compute monthly premium for an AD&D rate schedule.
 * AD&D schedules are always flat rate with 1-3 rates (employee, spouse/child/dependent).
 * Premium per rate = rate × (volume / divisor), same as flat rate calculation.
 *
 * When allSchedules is provided and an AD&D rate has no volume, the exposure
 * (volume or numberOfLives) is inherited from the corresponding Life rate
 * schedule (same plan). This handles the case where AD&D is "Included" and
 * shares the Life benefit's exposure.
 */
export const computeAdndPremium = (
  rateSchedule: RateSchedule,
  allSchedules?: RateSchedule[],
): number => {
  const rates = rateSchedule.rates || [];
  if (rates.length === 0) return 0;

  const primarySchedule = allSchedules?.find(
    (schedule) =>
      schedule.rateCoverageIndicator !== "adnd" &&
      !isChildCompanionSchedule(schedule, allSchedules),
  );

  const childCompanionSchedule = allSchedules
    ?.toReversed() // want to find the most recent child companion schedule if multiple exist, so reverse to find last match first
    .find((schedule) => isChildCompanionSchedule(schedule, allSchedules));

  return rates.reduce((total, rate) => {
    const rateValue = rate.rate ?? 0;
    let exposure = rate.volume ?? 0;

    // Child AD&D can inherit exposure from the separate child companion schedule.
    if (
      exposure === 0 &&
      rate.coverageTierCode === CoverageTierEnum.childOnly &&
      childCompanionSchedule
    ) {
      exposure = getAdndExposureFromPrimarySchedule(
        rate,
        childCompanionSchedule,
      );
    }

    // Otherwise inherit exposure from the primary life schedule.
    if (exposure === 0 && primarySchedule) {
      exposure = getAdndExposureFromPrimarySchedule(rate, primarySchedule);
    }

    if (rateValue === 0 || exposure === 0) return total;
    const divisor = getRateDivisor(effectiveUnit(rate));
    return total + rateValue * (exposure / divisor);
  }, 0);
};

/**
 * Check if rate schedule has gender-split rates
 */
export const hasGenderSplit = (rateSchedule: RateSchedule): boolean => {
  const rates = rateSchedule.rates || [];
  return rates.some((r) => r.gender != null);
};

/**
 * Compute gender-split FMLA PEPM premium
 * Sums premiums for male and female separately:
 * Total = (Male Total PEPM × Male W2 Employees) + (Female Total PEPM × Female W2 Employees)
 */
export const computeGenderSplitFmlaPepmPremium = (
  rateSchedule: RateSchedule,
): number => {
  const rates = rateSchedule.rates || [];

  // Find male rates
  const maleFmlaRate = rates.find(
    (r) => r.unit === RateUnitEnum.fmlaPepm && r.gender === "male",
  );
  const maleTotalRate = rates.find(
    (r) => r.unit === RateUnitEnum.totalPepm && r.gender === "male",
  );
  const maleAdaRate = rates.find(
    (r) => r.unit === RateUnitEnum.adaPepm && r.gender === "male",
  );

  // Find female rates
  const femaleFmlaRate = rates.find(
    (r) => r.unit === RateUnitEnum.fmlaPepm && r.gender === "female",
  );
  const femaleTotalRate = rates.find(
    (r) => r.unit === RateUnitEnum.totalPepm && r.gender === "female",
  );
  const femaleAdaRate = rates.find(
    (r) => r.unit === RateUnitEnum.adaPepm && r.gender === "female",
  );

  // Calculate male premium
  const maleTotalPepm =
    maleTotalRate?.rate ?? (maleFmlaRate?.rate ?? 0) + (maleAdaRate?.rate ?? 0);
  const maleW2Employees = maleFmlaRate?.numberOfLives ?? 0;
  const malePremium = maleTotalPepm * maleW2Employees;

  // Calculate female premium
  const femaleTotalPepm =
    femaleTotalRate?.rate ??
    (femaleFmlaRate?.rate ?? 0) + (femaleAdaRate?.rate ?? 0);
  const femaleW2Employees = femaleFmlaRate?.numberOfLives ?? 0;
  const femalePremium = femaleTotalPepm * femaleW2Employees;

  return malePremium + femalePremium;
};

/**
 * Compute monthly premium for FMLA PEPM rate schedule
 * Premium = Total PEPM × Number of W2 Employees
 * (Total PEPM is stored as a rate and auto-calculated as FMLA PEPM + ADA PEPM)
 *
 * For gender-split schedules, delegates to computeGenderSplitFmlaPepmPremium
 */
export const computeFmlaPepmPremium = (rateSchedule: RateSchedule): number => {
  const rates = rateSchedule.rates || [];
  if (rates.length === 0) return 0;

  // Check for gender split - delegate to separate function
  if (hasGenderSplit(rateSchedule)) {
    return computeGenderSplitFmlaPepmPremium(rateSchedule);
  }

  // Non-gender: single premium calculation
  const fmlaRate = rates.find((r) => r.unit === RateUnitEnum.fmlaPepm);
  const totalRate = rates.find((r) => r.unit === RateUnitEnum.totalPepm);

  // Use stored total PEPM, or fall back to calculating from FMLA + ADA
  const totalPepm =
    totalRate?.rate ??
    (fmlaRate?.rate ?? 0) +
      (rates.find((r) => r.unit === RateUnitEnum.adaPepm)?.rate ?? 0);

  // Get number of W2 employees from FMLA rate's numberOfLives or schedule level
  const numberOfW2Employees =
    fmlaRate?.numberOfLives ?? rateSchedule.numberOfLives ?? 0;

  return totalPepm * numberOfW2Employees;
};

/**
 * Compute monthly premium for Standard PEPM rate schedule (dental, vision, eap, std)
 * Premium = rate × numberOfLives (from the totalPepm rate)
 *
 * For gender-split schedules, sums premiums for male and female:
 * Total = (Male Rate × Male Enrolled) + (Female Rate × Female Enrolled)
 */
export const computeStandardPepmPremium = (
  rateSchedule: RateSchedule,
): number => {
  const rates = rateSchedule.rates || [];

  // Check for gender split
  if (hasGenderSplit(rateSchedule)) {
    const maleTotalPepmRate = rates.find(
      (r) => r.unit === RateUnitEnum.totalPepm && r.gender === "male",
    );
    const femaleTotalPepmRate = rates.find(
      (r) => r.unit === RateUnitEnum.totalPepm && r.gender === "female",
    );

    const malePepm = maleTotalPepmRate?.rate ?? 0;
    const maleLives = maleTotalPepmRate?.numberOfLives ?? 0;
    const malePremium = malePepm * maleLives;

    const femalePepm = femaleTotalPepmRate?.rate ?? 0;
    const femaleLives = femaleTotalPepmRate?.numberOfLives ?? 0;
    const femalePremium = femalePepm * femaleLives;

    return malePremium + femalePremium;
  }

  // Non-gender: single premium calculation
  const totalPepmRate = rates.find((r) => r.unit === RateUnitEnum.totalPepm);
  if (!totalPepmRate) return 0;

  const pepm = totalPepmRate.rate ?? 0;
  const numberOfLives = totalPepmRate.numberOfLives ?? 0;
  return pepm * numberOfLives;
};

/**
 * Check if rate schedule is Standard PEPM (dental, vision, eap, std)
 * Both FMLA and Standard PEPM have all 3 rates (fmlaPepm, adaPepm, totalPepm).
 * Standard PEPM stores numberOfLives on totalPepm rate, FMLA stores it on fmlaPepm rate.
 * We distinguish by checking where numberOfLives is stored (or expected to be stored).
 */
export const isStandardPepmSchedule = (rateSchedule: RateSchedule): boolean => {
  const rates = rateSchedule.rates || [];
  const totalPepmRate = rates.find((r) => r.unit === RateUnitEnum.totalPepm);
  const fmlaPepmRate = rates.find((r) => r.unit === RateUnitEnum.fmlaPepm);

  // If no totalPepm rate, not a PEPM schedule
  if (!totalPepmRate) return false;

  // Standard PEPM: numberOfLives is on totalPepm (can be 0 or positive)
  // FMLA PEPM: numberOfLives is on fmlaPepm (can be 0 or positive)
  // If fmlaPepm has numberOfLives set (even 0), it's FMLA. Otherwise Standard.
  const fmlaHasLives =
    fmlaPepmRate?.numberOfLives != null && fmlaPepmRate.numberOfLives > 0;
  const totalHasLives = totalPepmRate.numberOfLives != null;

  // If totalPepm has numberOfLives defined and fmlaPepm doesn't have lives, it's Standard PEPM
  return totalHasLives && !fmlaHasLives;
};

/**
 * Check if a rate unit is volume-based (volumePer*, per*OfSalary).
 */
export const isVolumeBasedUnit = (unit?: string): boolean =>
  [
    "volumePer10",
    "volumePer100",
    "volumePer1000",
    "per10OfSalary",
    "per100OfSalary",
    "per1000OfSalary",
  ].includes(unit ?? "");

/** Whether a rate row is priced on volume (vs. per person), from its own unit. */
export const isVolumeBasedRate = (
  rate: Pick<Rate, "unit"> | undefined,
): boolean =>
  rate?.unit != null && rate.unit !== "usd" && isVolumeBasedUnit(rate.unit);

/**
 * Compute monthly premium by summing (rate × numberOfLives) for all rates.
 * For volume-based units, computes rate × (volume / divisor) instead.
 * This is the common calculation used by composite, age banded, and related rate designs.
 */
export const computeRateLivesPremium = (rateSchedule: RateSchedule): number => {
  const rates = rateSchedule.rates || [];
  if (rates.length === 0) return 0;
  return rates.reduce((total, rate) => {
    const rateValue = rate.rate ?? 0;
    if (isVolumeBasedUnit(rate.unit)) {
      return total + computeVolumeUnitRatePremium(rate);
    }

    const lives = rate.numberOfLives ?? 0;
    return total + rateValue * lives;
  }, 0);
};

/**
 * Check if rate schedule is FMLA PEPM (numberOfLives stored on fmlaPepm rate)
 */
export const isFmlaPepmSchedule = (rateSchedule: RateSchedule): boolean => {
  const rates = rateSchedule.rates || [];
  const fmlaPepmRate = rates.find((r) => r.unit === RateUnitEnum.fmlaPepm);
  // FMLA PEPM has numberOfLives on the fmlaPepm rate
  return fmlaPepmRate != null && (fmlaPepmRate.numberOfLives ?? 0) > 0;
};

/**
 * Compute monthly premium based on rate design type.
 * Pass allSchedules so AD&D schedules can inherit volume from their Life schedule.
 */
export const computeMonthlyPremium = (
  rateSchedule: RateSchedule,
  allSchedules?: RateSchedule[],
): number => {
  // AD&D schedules are always flat rate — use dedicated calculation that sums all rates
  if (rateSchedule.rateCoverageIndicator === "adnd") {
    return computeAdndPremium(rateSchedule, allSchedules);
  }

  // Child companion schedules store their exposure in numberOfLives, so they
  // must not go through the volume-only flat rate path.
  if (isChildCompanionSchedule(rateSchedule, allSchedules ?? [])) {
    return computeChildCompanionPremium(rateSchedule);
  }

  switch (rateSchedule.rateDesign) {
    case "flatRate":
      // Check Standard PEPM first (has totalPepm rate with numberOfLives on it)
      // This must be checked before FMLA PEPM since both have fmlaPepm rates now
      if (isStandardPepmSchedule(rateSchedule)) {
        return computeStandardPepmPremium(rateSchedule);
      }
      // Check if this is FMLA PEPM (has fmlaPepm rate with numberOfLives on it)
      if (isFmlaPepmSchedule(rateSchedule)) {
        return computeFmlaPepmPremium(rateSchedule);
      }
      return computeFlatRatePremium(rateSchedule);
    case "composite":
    case "ageBanded":
    case "ageBandedTobacco":
    case "ageBandedGender":
    case "ageBandedTiered":
      return computeRateLivesPremium(rateSchedule);
    // TODO: Add other rate designs as needed
    default:
      return 0;
  }
};
